import React, { useCallback, useMemo, useState, useRef } from "react"
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  Connection,
  addEdge,
  reconnectEdge,
  MarkerType,
  BackgroundVariant,
  OnNodesChange,
  OnEdgesChange,
  useReactFlow
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"

import { BranchNodeData, WorkflowNode, WorkflowEdge } from "lib/types/workflow"
import { nodeTypes } from "./nodes"
import AddNodeDialog, { NodeChoice } from "./AddNodeDialog"

import { CanvasContainer } from "./WorkflowCanvas.styled"

import {
  FIT_VIEW_PADDING,
  TerminalTarget,
  edgeTypes
} from "./constants"
import {
  findClosestEdge,
  findClosestTerminal
} from "./helpers/geometry"
import {
  ACTION,
  BRANCH,
  branchCaseHandle,
  CONDITION,
  HANDLE_BRANCH_OTHERWISE,
  HANDLE_INPUT,
  HANDLE_OUTPUT,
  HANDLE_OTHERWISE,
  HANDLE_THEN,
  MAX_PARALLEL_CHILDREN,
  NODE_TYPE_BRANCH,
  TRIGGER
} from "../nodeKind"
import { makeEdge } from "./helpers/edges"
import { computeLayout } from "./helpers/layout"
import {
  createActionNode,
  createBranchCase,
  createBranchNode,
  createConditionNode
} from "./helpers/nodeFactories"

type Props = {
  nodes: WorkflowNode[]
  edges: WorkflowEdge[]
  onNodesChange: OnNodesChange<WorkflowNode>
  onEdgesChange: OnEdgesChange<WorkflowEdge>
  setNodes: React.Dispatch<React.SetStateAction<WorkflowNode[]>>
  setEdges: React.Dispatch<React.SetStateAction<WorkflowEdge[]>>
  onNodeClick?: (event: React.MouseEvent, node: WorkflowNode) => void
  /** Fired after the inline "+" on a BRANCH card appends a new case. The
   *  parent uses this to open the sidebar editor and focus the new case's
   *  name field. Receives the updated branch node and the freshly minted
   *  case id. */
  onBranchCaseAdded?: (node: WorkflowNode, newCaseId: string) => void
  readOnly?: boolean
}

const WorkflowCanvas = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  setNodes,
  setEdges,
  onNodeClick,
  onBranchCaseAdded,
  readOnly = false
}: Props) => {
  const [dropTargetEdgeId, setDropTargetEdgeId] = useState<string | null>(null)
  const [dropTargetTerminal, setDropTargetTerminal] = useState<TerminalTarget | null>(
    null
  )
  const [animating, setAnimating] = useState(false)
  const [insertEdgeId, setInsertEdgeId] = useState<string | null>(null)
  const [insertTerminal, setInsertTerminal] = useState<{
    nodeId: string
    handleId: string
  } | null>(null)
  const draggedRef = useRef<string | null>(null)
  const { fitView } = useReactFlow()

  const appendNodeAtTerminal = useCallback(
    (parentId: string, sourceHandle: string, newNode: WorkflowNode) => {
      const edge = makeEdge(parentId, sourceHandle, newNode.id, HANDLE_INPUT)
      const nextNodes = [...nodes, newNode]
      const nextEdges = [...edges, edge]

      setNodes(nextNodes)
      setEdges(nextEdges)

      setAnimating(true)
      const layout = computeLayout(nextNodes, nextEdges)
      setNodes((nds) =>
        nds.map((n) => {
          const pos = layout.get(n.id)
          return pos ? { ...n, position: pos } : n
        })
      )
      setTimeout(() => {
        fitView({ duration: 400, padding: FIT_VIEW_PADDING })
        setAnimating(false)
      }, 350)
    },
    [nodes, edges, setNodes, setEdges, fitView]
  )

  const onAddAtTerminal = useCallback(
    (nodeId: string, handleId: string) => {
      setInsertTerminal({ nodeId, handleId })
    },
    []
  )

  const insertNodeOnEdge = useCallback(
    (edgeId: string, newNode: WorkflowNode) => {
      const targetEdge = edges.find((e) => e.id === edgeId)
      if (!targetEdge) return

      const outputHandle =
        newNode.type === CONDITION
          ? HANDLE_THEN
          : newNode.type === BRANCH
          ? branchCaseHandle((newNode.data as BranchNodeData).cases[0].id)
          : HANDLE_OUTPUT

      const newEdges = [
        ...edges.filter((e) => e.id !== edgeId),
        makeEdge(targetEdge.source, targetEdge.sourceHandle, newNode.id, HANDLE_INPUT),
        makeEdge(newNode.id, outputHandle, targetEdge.target, targetEdge.targetHandle)
      ]

      const nextNodes = [...nodes, newNode]
      setNodes(nextNodes)
      setEdges(newEdges)

      setAnimating(true)
      const layout = computeLayout(nextNodes, newEdges)
      setNodes((nds) =>
        nds.map((n) => {
          const pos = layout.get(n.id)
          return pos ? { ...n, position: pos } : n
        })
      )
      setTimeout(() => {
        fitView({ duration: 400, padding: FIT_VIEW_PADDING })
        setAnimating(false)
      }, 350)
    },
    [edges, nodes, setEdges, setNodes, fitView]
  )

  const handleAddNodeSelect = useCallback(
    (choice: NodeChoice) => {
      const newNode =
        choice === CONDITION
          ? createConditionNode()
          : choice === BRANCH
          ? createBranchNode()
          : createActionNode()
      if (insertEdgeId) {
        insertNodeOnEdge(insertEdgeId, newNode)
        setInsertEdgeId(null)
      } else if (insertTerminal) {
        appendNodeAtTerminal(insertTerminal.nodeId, insertTerminal.handleId, newNode)
        setInsertTerminal(null)
      }
    },
    [insertEdgeId, insertTerminal, insertNodeOnEdge, appendNodeAtTerminal]
  )

  /** Append a fresh empty case to a BRANCH node and reflow the graph. The
   *  optional `onBranchCaseAdded` callback lets the parent open the sidebar
   *  editor and focus the new case's name field. */
  const onAddCase = useCallback(
    (nodeId: string) => {
      const target = nodes.find((n) => n.id === nodeId)
      if (!target || target.data.nodeType !== NODE_TYPE_BRANCH) return
      const branchData = target.data as BranchNodeData
      if (branchData.cases.length >= MAX_PARALLEL_CHILDREN) return
      const nextCase = createBranchCase(`Case ${branchData.cases.length + 1}`)
      const updatedBranch = {
        ...target,
        data: {
          ...target.data,
          cases: [...branchData.cases, nextCase]
        }
      } as WorkflowNode
      const nextNodes = nodes.map((n) => (n.id === nodeId ? updatedBranch : n))
      // The card's width depends on case count, so reflow in the same pass
      // so siblings don't collide with the wider BRANCH card.
      const layout = computeLayout(nextNodes, edges)
      setNodes(
        nextNodes.map((n) => {
          const pos = layout.get(n.id)
          return pos ? { ...n, position: pos } : n
        })
      )
      onBranchCaseAdded?.(updatedBranch, nextCase.id)
    },
    [nodes, edges, setNodes, onBranchCaseAdded]
  )

  const terminalHandlesByNode = useMemo(() => {
    const SOURCE_HANDLES: Record<string, string[]> = {
      [TRIGGER]: [HANDLE_OUTPUT],
      [CONDITION]: [HANDLE_THEN, HANDLE_OTHERWISE],
      [ACTION]: [HANDLE_OUTPUT]
    }
    // Pre-tally outgoing edges per (node, handle) so we can hide the "+ Add"
    // button on handles that are already at the API's parallel-children cap.
    const edgeCount = new Map<string, number>()
    for (const e of edges) {
      const key = `${e.source}::${e.sourceHandle ?? HANDLE_OUTPUT}`
      edgeCount.set(key, (edgeCount.get(key) ?? 0) + 1)
    }
    // Every node kind accepts multiple parallel children (the backend stores
    // a `then[]` / `otherwise[]` array on each node, up to MAX_PARALLEL_CHILDREN
    // entries) — keep the "+ Add" button visible until that cap is hit.
    const result = new Map<string, string[]>()
    for (const node of nodes) {
      const candidates =
        node.type === BRANCH
          ? [
              ...(node.data as BranchNodeData).cases.map((c) =>
                branchCaseHandle(c.id)
              ),
              HANDLE_BRANCH_OTHERWISE
            ]
          : SOURCE_HANDLES[node.type ?? ""] ?? []
      const open = candidates.filter(
        (h) => (edgeCount.get(`${node.id}::${h}`) ?? 0) < MAX_PARALLEL_CHILDREN
      )
      if (open.length > 0) result.set(node.id, open)
    }
    return result
  }, [nodes, edges])

  const displayNodes = useMemo(
    () =>
      nodes.map((n) => {
        const isBranch = n.type === BRANCH
        const branchAtCap =
          isBranch &&
          (n.data as BranchNodeData).cases.length >= MAX_PARALLEL_CHILDREN
        return {
          ...n,
          data: {
            ...n.data,
            _terminalHandles: readOnly ? [] : (terminalHandlesByNode.get(n.id) ?? []),
            _onAddAtTerminal: readOnly ? undefined : onAddAtTerminal,
            // Only inject the inline "+ case" handler when the branch can
            // accept another case — once at the cap, the BranchNode hides
            // the button instead of letting the user trip an API error.
            _onAddCase:
              readOnly || !isBranch || branchAtCap ? undefined : onAddCase,
            _activeTerminalDropHandle:
              !readOnly && dropTargetTerminal?.nodeId === n.id
                ? dropTargetTerminal.handleId
                : undefined
          }
        }
      }),
    [
      nodes,
      terminalHandlesByNode,
      onAddAtTerminal,
      onAddCase,
      dropTargetTerminal,
      readOnly
    ]
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      const edge = {
        ...connection,
        markerEnd: { type: MarkerType.ArrowClosed },
        animated: true
      }
      setEdges((eds) => addEdge(edge, eds))
    },
    [setEdges]
  )

  const onReconnect = useCallback(
    (oldEdge: WorkflowEdge, newConnection: Connection) => {
      setEdges((eds) => reconnectEdge(oldEdge, newConnection, eds))
    },
    [setEdges]
  )

  const isValidConnection = useCallback(
    (connection: WorkflowEdge | Connection) =>
      // Every source handle on every node kind accepts multiple parallel
      // children — the only invalid connection is a node connecting to itself.
      connection.source !== connection.target,
    []
  )

  const onNodeDragStart = useCallback(
    (_event: React.MouseEvent, node: WorkflowNode) => {
      if (node.type === TRIGGER) return
      draggedRef.current = node.id
    },
    []
  )

  const onNodeDrag = useCallback(
    (_event: React.MouseEvent, node: WorkflowNode) => {
      if (!draggedRef.current) return
      const terminal = findClosestTerminal(
        node.position,
        draggedRef.current,
        nodes,
        terminalHandlesByNode
      )
      if (terminal) {
        setDropTargetTerminal(terminal)
        setDropTargetEdgeId(null)
        return
      }
      setDropTargetTerminal(null)
      const closest = findClosestEdge(node.position, draggedRef.current, nodes, edges)
      setDropTargetEdgeId(closest)
    },
    [nodes, edges, terminalHandlesByNode]
  )

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent) => {
      const draggedId = draggedRef.current
      const targetEdgeId = dropTargetEdgeId
      const targetTerminal = dropTargetTerminal

      draggedRef.current = null
      setDropTargetEdgeId(null)
      setDropTargetTerminal(null)

      if (!draggedId) return

      const draggedNode = nodes.find((n) => n.id === draggedId)
      if (!draggedNode) return

      if (targetTerminal) {
        if (targetTerminal.nodeId === draggedId) return

        const incoming = edges.filter((e) => e.target === draggedId)
        const outgoing = edges.filter((e) => e.source === draggedId)

        const edgesToRemove = new Set<string>()
        for (const e of incoming) edgesToRemove.add(e.id)
        for (const e of outgoing) edgesToRemove.add(e.id)

        const edgesToAdd: WorkflowEdge[] = []

        if (incoming.length === 1 && outgoing.length === 1) {
          edgesToAdd.push(
            makeEdge(
              incoming[0].source,
              incoming[0].sourceHandle,
              outgoing[0].target,
              outgoing[0].targetHandle
            )
          )
        }

        edgesToAdd.push(
          makeEdge(
            targetTerminal.nodeId,
            targetTerminal.handleId,
            draggedId,
            HANDLE_INPUT
          )
        )

        const newEdges = [
          ...edges.filter((e) => !edgesToRemove.has(e.id)),
          ...edgesToAdd
        ]
        setEdges(newEdges)

        setAnimating(true)
        const layout = computeLayout(nodes, newEdges)
        setNodes((nds) =>
          nds.map((n) => {
            const pos = layout.get(n.id)
            return pos ? { ...n, position: pos } : n
          })
        )
        setTimeout(() => {
          fitView({ duration: 400, padding: FIT_VIEW_PADDING })
          setAnimating(false)
        }, 350)
        return
      }

      if (!targetEdgeId) return

      const targetEdge = edges.find((e) => e.id === targetEdgeId)
      if (!targetEdge) return

      const outputHandle =
        draggedNode.type === CONDITION
          ? HANDLE_THEN
          : draggedNode.type === BRANCH
          ? branchCaseHandle((draggedNode.data as BranchNodeData).cases[0].id)
          : HANDLE_OUTPUT

      const incoming = edges.filter((e) => e.target === draggedId)
      const outgoing = edges.filter((e) => e.source === draggedId)

      const edgesToRemove = new Set<string>([targetEdge.id])
      const edgesToAdd: WorkflowEdge[] = []

      for (const e of incoming) edgesToRemove.add(e.id)
      for (const e of outgoing) edgesToRemove.add(e.id)

      if (incoming.length === 1 && outgoing.length === 1) {
        edgesToAdd.push(
          makeEdge(
            incoming[0].source,
            incoming[0].sourceHandle,
            outgoing[0].target,
            outgoing[0].targetHandle
          )
        )
      }

      edgesToAdd.push(
        makeEdge(targetEdge.source, targetEdge.sourceHandle, draggedId, HANDLE_INPUT),
        makeEdge(draggedId, outputHandle, targetEdge.target, targetEdge.targetHandle)
      )

      const newEdges = [
        ...edges.filter((e) => !edgesToRemove.has(e.id)),
        ...edgesToAdd
      ]
      setEdges(newEdges)

      setAnimating(true)
      const layout = computeLayout(nodes, newEdges)
      setNodes((nds) =>
        nds.map((n) => {
          const pos = layout.get(n.id)
          return pos ? { ...n, position: pos } : n
        })
      )
      setTimeout(() => {
        fitView({ duration: 400, padding: FIT_VIEW_PADDING })
        setAnimating(false)
      }, 350)
    },
    [
      dropTargetEdgeId,
      dropTargetTerminal,
      edges,
      nodes,
      setEdges,
      setNodes,
      fitView
    ]
  )

  const onAddClick = useCallback((edgeId: string) => {
    setInsertEdgeId(edgeId)
  }, [])

  const displayEdges = useMemo(() => {
    return edges.map((e) => {
      const base: WorkflowEdge = {
        ...e,
        type: "addNode",
        data: { ...(e.data ?? {}), onAddClick: readOnly ? undefined : onAddClick }
      }
      if (!readOnly && e.id === dropTargetEdgeId) {
        return {
          ...base,
          className: "drop-target",
          label: "⊕ Drop to insert here",
          labelStyle: {
            fontSize: 12,
            fontWeight: 700,
            color: "#10b981",
            background: "#ecfdf5",
            padding: "4px 10px",
            borderRadius: "12px",
            border: "2px solid #10b981"
          },
          labelBgStyle: { display: "none" }
        }
      }
      return base
    })
  }, [edges, dropTargetEdgeId, onAddClick, readOnly])

  const memoNodeTypes = useMemo(() => nodeTypes, [])
  const memoEdgeTypes = useMemo(() => edgeTypes, [])

  return (
    <CanvasContainer className={animating ? "animating" : ""}>
      <ReactFlow<WorkflowNode, WorkflowEdge>
        nodes={displayNodes}
        edges={displayEdges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={readOnly ? undefined : onConnect}
        onReconnect={readOnly ? undefined : onReconnect}
        onNodeClick={onNodeClick}
        onNodeDragStart={readOnly ? undefined : onNodeDragStart}
        onNodeDrag={readOnly ? undefined : onNodeDrag}
        onNodeDragStop={readOnly ? undefined : onNodeDragStop}
        isValidConnection={readOnly ? () => false : isValidConnection}
        edgesReconnectable={!readOnly}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        nodeTypes={memoNodeTypes}
        edgeTypes={memoEdgeTypes}
        connectOnClick={!readOnly}
        connectionRadius={40}
        fitView
        fitViewOptions={{ padding: FIT_VIEW_PADDING }}
        deleteKeyCode={readOnly ? null : ["Backspace", "Delete"]}
        proOptions={{ hideAttribution: true }}
      >
        <Controls />
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <MiniMap />
      </ReactFlow>
      {!readOnly && (insertEdgeId || insertTerminal) && (
        <AddNodeDialog
          onSelect={handleAddNodeSelect}
          onClose={() => {
            setInsertEdgeId(null)
            setInsertTerminal(null)
          }}
        />
      )}
    </CanvasContainer>
  )
}

export default WorkflowCanvas
